<template>
  <div class="contact-info" :class="{ collapsed: !isVisible }">
   <div
          class="toggle-row"
        >
          <div
            class="toggle-button"
            @click="emit('toggleVisibility')"
            :title="t('cooperation.contact.minimize')"
          >
            <span class="toggle-icon">一</span>
            <!-- <span class="toggle-text">{{ props.isVisible ? t('cooperation.contact.minimize') : t('cooperation.contact.maximize') }}</span> -->
          </div>
        </div>
    <transition name="collapse">
      <div v-if="isVisible" class="contact-content">
        <div class="locations">
          <div 
            v-for="location in props.locations" 
            :key="location.id"
            class="location-item"
            :class="{ active: currentLocationId === location.id }"
            @click="switchLocation(location.id)"
          >
            <div class="location-name">{{ location.name }}，</div>
            <span class="location-country">{{ location.country }}</span>
          </div>
        </div>

        <div class="contact-details">
          <div v-if="currentLocationDetails" class="detail-item">
            
            <div class="detail-label"><el-icon><location-filled /></el-icon>{{ t('cooperation.contact.address') }}</div>
            <div class="detail-value">
              {{ currentLocationDetails.address }}
              <template v-if="currentLocationDetails.addressLine2">
                <br />
                {{ currentLocationDetails.addressLine2 }}
              </template>
            </div>
          </div>

          <div v-if="currentLocationDetails" class="divider"></div>

          <div v-if="currentLocationDetails" class="detail-item">
              
            <div class="detail-label"><el-icon><phone /></el-icon>{{ t('cooperation.contact.phone') }}</div>
            <div class="detail-value">{{ currentLocationDetails.phone }}</div>
          </div>

          <div v-if="currentLocationDetails" class="divider"></div>

          <div v-if="currentLocationDetails" class="detail-item">
            <div class="detail-label"><el-icon><message /></el-icon>{{ t('cooperation.contact.email') }}</div>
            <div class="detail-value">{{ currentLocationDetails.email }}</div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, watch } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(['toggleVisibility', 'locationChange']);
const { t } = useI18n();

// 定义组件属性，支持从外部传入数据
const props = defineProps({
  isVisible: {
    type: Boolean,
    default: true
  },
  // 地点列表
  locations: {
    type: Array,
    default: () => [
      { id: 'boston', name: '波士顿', country: '美国' },
      { id: 'suzhou', name: '苏州', country: '中国' },
      { id: 'wuxi', name: '无锡', country: '中国' }
    ]
  },
  // 地点详情数据
  locationDetails: {
    type: Object,
    default: () => ({
    })
  },
  // 默认选中的地点ID
  defaultLocationId: {
    type: String,
    default: 'wenzhou'
  }
})

// 当前选中的地点ID
const currentLocationId = ref(props.defaultLocationId);

// 当前地点的详细信息
const currentLocationDetails = computed(() => {
  return props.locationDetails[currentLocationId.value] || null;
});
const isVisible = computed(() => {
  return props.isVisible;
});

// 监听locations变化，当locations变化时，选中第一个
watch(
  () => props.locations,
  (newLocations) => {
    if (newLocations.length > 0) {
      const firstLocationId = newLocations[0].id;
      currentLocationId.value = firstLocationId;
      emit('locationChange', firstLocationId);
    }
  },
  { deep: true, immediate: true }
);

// 切换地点
const switchLocation = (locationId) => {
  currentLocationId.value = locationId;
  emit('locationChange', locationId);
};
</script>

<style scoped>
.contact-info {
  width: 100%;
  /* padding: 1rem 2rem 2rem; */
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: all 1s ease;
}

/* 折叠状态样式 */
.contact-info.collapsed {
  transform: scale(0);
  transform-origin: 95% 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.toggle-row {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  justify-content: flex-end;
}
/* 切换按钮样式 */
.toggle-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #f0f9ff;
  border: 1px solid #e6f7ff;
  color: #004a99;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  width: fit-content;
  /* 在右上角 */

  /* float: right; */
}
.toggle-button:hover {
  background-color: #e6f7ff;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
}

.toggle-icon {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

/* 过渡动画 */
.collapse-enter-active,
.collapse-leave-active {
  transition: opacity 1s ease, transform 1s ease, max-height 1s ease;
}

.collapse-enter-from {
  opacity: 0;
  transform: scaleX(0.9) scaleY(0.9);
  transform-origin: top right;
  /* max-height: 0; */
}

.collapse-leave-to {
  opacity: 0;
  transform: scaleX(0.9) scaleY(0.9);
  transform-origin: top right;
  /* max-height: 0; */
}

.contact-content {
  overflow: hidden;
  padding: 1rem 2rem 2rem;
  transform-origin: top right;
}

.locations {
  display: flex;
  gap: 2rem;
  margin: 0 0 2rem 0;
  flex-wrap: wrap;
}
.location-item.active {
  color: #004a99;
  border-bottom: 2px solid #004a99;
  padding-bottom: 0.25rem;
  .location-name,
  .location-country {
    color: #004a99;
  }
}

.location-item {
  cursor: pointer;
  transition: all 0.3s ease;
  max-width: 100px;
}

.location-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #999;
  transition: color 0.3s ease;
}

.location-country {
  font-size: 1.5rem;
  color: #999;
}



.contact-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.detail-item i {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  /* color: #1890ff; */
  flex-shrink: 0;
  margin-top: 2px;
}

/* .icon-location::before {
  content: "📍";
  font-size: 1.25rem;
}

.icon-phone::before {
  content: "☎️";
  font-size: 1.25rem;
}

.icon-email::before {
  content: "📧";
  font-size: 1.25rem;
} */

.detail-label {
  font-weight: 500;
  color: #2c3e50;
  /* min-width: 50px; */
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.detail-value {
  color: #666;
  line-height: 1.6;
}

.divider {
  height: 1px;
  background-color: #e0e0e0;
  width: 100%;
  margin: 0.5rem 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .contact-info {
    padding: 1rem 1.5rem 1.5rem;
  }

  .contact-content {
    padding: 3rem 1rem 1.5rem;
  }

  .locations {
    flex-direction: column;
    gap: 1rem;
  }

  .location-name,
  .location-country {
    font-size: 1.25rem;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .detail-label {
    margin-bottom: 0.25rem;
  }
}

@media (max-width: 480px) {
  .contact-info {
    padding: 0.75rem 1rem 1rem;
    border-radius: 8px;
  }

  .contact-content {
    padding: 1rem 0.75rem 1rem;
  }

  .toggle-button {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
    top: 0.4rem;
    right: 0.4rem;
  }

  .location-name,
  .location-country {
    font-size: 1.1rem;
  }
}
</style>
